import {
    Button,
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@jhool-io/fe-components";
import { useState } from "react";
import {
    useFetchUserDetails,
    useFetchUserPractices,
} from "../../modules/auth/hooks/auth.queries";
import { IPractice } from "../../modules/auth/types/auth.types";
import {
    cn,
    getClientDisplayName,
    getClientInitials,
    truncateString,
} from "../../utils/helpers";
import useCurrentClientProfile from "../../hooks/useCurrentClient";
import axiosInstance from "../../utils/axios";
import Avatar from "../Avatar/Avatar";
import ArrowDownIcon from "../Icons/ArrowDown";
import { handleChangePractice } from "../../redux/features/practice/practiceSlice";
import { useAppDispatch } from "../../hooks/useRedux";
import useAuthContext from "../../hooks/useAuthContext";
import ChevronDownIcon from "../Icons/ChevronDown";
import Skeleton from "../Skeleton/Skeleton";
import {
    getClientSession,
    setClientSession,
} from "../../utils/clientSessionStore";

export default function ProfilePracticeSwitcher() {
    const { data: loggedInUserData, isSuccess } = useFetchUserDetails();
    const currentProfile = useCurrentClientProfile();
    const dispatch = useAppDispatch();
    const { logOutUser } = useAuthContext();

    const { userId } = getClientSession();

    const [expandedProfileId, setExpandedProfileId] = useState<string | null>(
        null
    );

    const allProfiles = loggedInUserData?.data
        ? [
              {
                  user_id: loggedInUserData.data.user_id,
                  first_name: loggedInUserData.data.first_name,
                  last_name: loggedInUserData.data.last_name,
                  preferred_name: loggedInUserData.data.preferred_name,
              },
              ...(loggedInUserData.data.sub_profiles || []),
          ]
        : [];

    const { data: expandedProfilePractices, isLoading: isLoadingPractices } =
        useFetchUserPractices(expandedProfileId || userId || "");

    const practices = expandedProfilePractices?.data?.practices || [];

    const handleToggleProfile = (profileUserId: string) => {
        setExpandedProfileId((prev) =>
            prev === profileUserId ? null : profileUserId
        );
    };

    const handleSelectPractice = (
        profileUserId: string,
        practice: IPractice
    ) => {
        const isSameProfileAndPractice =
            currentProfile?.userId === profileUserId &&
            currentProfile?.practice?.practice_id === practice.practice_id;

        if (isSameProfileAndPractice) {
            return;
        }

        axiosInstance.defaults.headers.common.client_id = practice.client_id;
        axiosInstance.defaults.headers.common.practice_id =
            practice.practice_id;

        setClientSession({
            clientId: practice.client_id,
            userId: profileUserId,
            practice,
        });

        dispatch(
            handleChangePractice({
                practice,
            })
        );
        window.location.href = "/";
    };

    const hasMultipleProfilesOrPractices =
        allProfiles.length > 1 ||
        (allProfiles.length === 1 &&
            expandedProfilePractices?.data?.practices &&
            expandedProfilePractices.data.practices.length > 1);

    return isSuccess && loggedInUserData && currentProfile ? (
        <Popover>
            <PopoverTrigger asChild>
                <div className="hidden md:flex items-center gap-8 cursor-pointer">
                    <Avatar
                        label={getClientInitials(
                            currentProfile.preferredName,
                            currentProfile.firstName,
                            currentProfile.lastName
                        )}
                    />
                    <span className="flex items-center gap-16">
                        <span className="text-base font-medium capitalize">
                            {getClientDisplayName(
                                currentProfile.preferredName,
                                currentProfile.firstName
                            )}
                        </span>
                        <ArrowDownIcon />
                    </span>
                </div>
            </PopoverTrigger>
            <PopoverContent
                align="end"
                className="w-[350px] max-h-[80vh] overflow-y-auto p-6"
            >
                {hasMultipleProfilesOrPractices ? (
                    <div className="mb-4">
                        <div className="flex flex-col gap-y-4">
                            {allProfiles.map((profile) => {
                                const isExpanded =
                                    expandedProfileId === profile.user_id;
                                const isSelected =
                                    currentProfile?.userId === profile.user_id;

                                return (
                                    <div key={profile.user_id}>
                                        <Button
                                            variant="normal"
                                            size="auto"
                                            type="button"
                                            onClick={() =>
                                                handleToggleProfile(
                                                    profile.user_id
                                                )
                                            }
                                            className={cn(
                                                "w-full flex items-center justify-between p-8 rounded-r8 transition-colors",
                                                isSelected
                                                    ? "bg-blue-100"
                                                    : "hover:bg-strokelight"
                                            )}
                                        >
                                            <div className="flex items-center gap-12">
                                                <span className="flex items-center justify-center bg-secondary-light w-32 h-32 rounded-full border-1.5 border-primary text-[1.28rem] font-medium">
                                                    {getClientInitials(
                                                        profile.preferred_name,
                                                        profile.first_name,
                                                        profile.last_name
                                                    )}
                                                </span>
                                                <span className="capitalize text-sm">
                                                    {getClientDisplayName(
                                                        profile.preferred_name,
                                                        profile.first_name,
                                                        profile.last_name,
                                                        "full_name"
                                                    )}
                                                </span>
                                            </div>
                                            <ChevronDownIcon
                                                stroke="#0b132b"
                                                className={cn(
                                                    "w-16 h-16 transition-transform",
                                                    isExpanded && "rotate-180"
                                                )}
                                            />
                                        </Button>

                                        {isExpanded && (
                                            <div className="ml-24 mt-8 mb-8 border-l-2 border-l-strokelight pl-12">
                                                {isLoadingPractices && (
                                                    <Skeleton className="h-32 w-full" />
                                                )}
                                                {!isLoadingPractices &&
                                                    practices.length > 0 && (
                                                        <div className="flex flex-col gap-y-2">
                                                            {practices.map(
                                                                (practice) => {
                                                                    const practiceSelected =
                                                                        currentProfile
                                                                            ?.practice
                                                                            ?.practice_id ===
                                                                            practice.practice_id &&
                                                                        currentProfile?.userId ===
                                                                            profile.user_id;

                                                                    const activeSessions =
                                                                        practice.active_sessions ??
                                                                        [];

                                                                    const activeProviderNames =
                                                                        Array.from(
                                                                            new Set(
                                                                                activeSessions.map(
                                                                                    (
                                                                                        session
                                                                                    ) =>
                                                                                        `${session.provider.first_name} ${session.provider.last_name}`
                                                                                )
                                                                            )
                                                                        ).join(
                                                                            ", "
                                                                        );

                                                                    return (
                                                                        <Button
                                                                            variant="normal"
                                                                            size="auto"
                                                                            key={
                                                                                practice.practice_id
                                                                            }
                                                                            onClick={() =>
                                                                                handleSelectPractice(
                                                                                    profile.user_id,
                                                                                    practice
                                                                                )
                                                                            }
                                                                            className={cn(
                                                                                "w-full flex items-center justify-between p-8 rounded-r8 transition-colors",
                                                                                practiceSelected
                                                                                    ? "bg-blue-100"
                                                                                    : "hover:bg-strokelight"
                                                                            )}
                                                                        >
                                                                            <div className="flex items-center gap-12">
                                                                                <div className="w-28 h-28 rounded-full bg-primary text-white flex items-center justify-center font-semibold text-xs shrink-0">
                                                                                    {practice.practice_name
                                                                                        ?.charAt(
                                                                                            0
                                                                                        )
                                                                                        .toUpperCase()}
                                                                                </div>
                                                                                <div className="flex flex-col items-start">
                                                                                    <span className="text-xs font-medium">
                                                                                        {
                                                                                            practice.practice_name
                                                                                        }
                                                                                    </span>
                                                                                    {practice
                                                                                        .active_sessions
                                                                                        ?.length >
                                                                                        0 && (
                                                                                        <span className="text-xs text-gray">
                                                                                            {truncateString(
                                                                                                activeProviderNames,
                                                                                                30
                                                                                            )}
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                            {practiceSelected && (
                                                                                <svg
                                                                                    className="w-16 h-16 text-primary shrink-0"
                                                                                    fill="currentColor"
                                                                                    viewBox="0 0 20 20"
                                                                                >
                                                                                    <path
                                                                                        fillRule="evenodd"
                                                                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                                                        clipRule="evenodd"
                                                                                    />
                                                                                </svg>
                                                                            )}
                                                                        </Button>
                                                                    );
                                                                }
                                                            )}
                                                        </div>
                                                    )}
                                                {!isLoadingPractices &&
                                                    practices.length === 0 && (
                                                        <div className="text-xs text-gray py-8 pl-12">
                                                            No practices
                                                            available
                                                        </div>
                                                    )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center gap-8 pb-16">
                        <Avatar
                            label={getClientInitials(
                                currentProfile.preferredName,
                                currentProfile.firstName,
                                currentProfile.lastName
                            )}
                        />

                        <span className=" text-base font-medium capitalize">
                            {getClientDisplayName(
                                currentProfile.preferredName,
                                currentProfile.firstName,
                                currentProfile.lastName,
                                "full_name"
                            )}
                        </span>
                    </div>
                )}

                <div className="pt-16 border-t border-t-[rgba(87,_106,_129,_0.15)]">
                    <Button
                        size="auto"
                        variant="normal"
                        onClick={() => logOutUser()}
                        className="text-sm text-danger font-medium justify-start"
                        aria-label="Log out button"
                    >
                        Logout
                    </Button>
                </div>
            </PopoverContent>
        </Popover>
    ) : null;
}
